import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../App';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 10) {
      if (attempts >= 10) setStatus('timeout');
      return;
    }

    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      setOrderData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
    } catch {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === 'checking' && attempts < 10) {
      const timer = setTimeout(pollStatus, attempts === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-surface-base">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-clash text-2xl text-white mb-2">Invalid Payment Session</h1>
          <p className="text-zinc-400 mb-6">No payment session found.</p>
          <Link to="/marketplace" className="btn-gold px-6 py-3 inline-flex items-center gap-2">
            Browse Marketplace <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />

      <div className="max-w-2xl mx-auto px-4 py-20">
        {status === 'checking' && (
          <div className="text-center" data-testid="payment-checking">
            <Loader2 className="w-16 h-16 text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-clash text-2xl text-white mb-2">Processing Payment</h1>
            <p className="text-zinc-400">Confirming your payment with Stripe...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center" data-testid="payment-success">
            <div className="w-20 h-20 bg-emerald-500/10 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="font-clash text-3xl text-white mb-2">Payment Successful!</h1>
            <p className="text-zinc-400 mb-8">Your purchase has been confirmed.</p>

            {orderData && (
              <div className="p-6 bg-surface-card border border-border-subtle text-left mb-8">
                <h2 className="font-clash text-lg text-white mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-gold" /> Order Summary
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Item</span>
                    <span className="text-white">{orderData.listing_title}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Item Price</span>
                    <span className="text-white">${orderData.item_price?.toLocaleString() || orderData.amount?.toLocaleString()}</span>
                  </div>
                  {orderData.buyer_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Service Fee</span>
                      <span className="text-white">${orderData.buyer_fee?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-border-subtle pt-3 flex justify-between">
                    <span className="text-zinc-400">Total Paid</span>
                    <span className="text-gold font-clash font-semibold">${orderData.amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Status</span>
                    <span className="text-emerald-500 font-medium">Confirmed</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard" className="btn-gold px-6 py-3 flex items-center justify-center gap-2">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/marketplace" className="btn-secondary px-6 py-3">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="text-center" data-testid="payment-error">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-clash text-2xl text-white mb-2">
              {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
            </h1>
            <p className="text-zinc-400 mb-6">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We could not confirm your payment. If you were charged, please contact support.'}
            </p>
            <Link to="/marketplace" className="btn-gold px-6 py-3 inline-flex items-center gap-2">
              Back to Marketplace <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
